import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ProfileLogoInfo = {
  id: string;
  url: string;
};

export interface NavState {
  isExpanded: boolean;
  toggle: () => void;
  shortcut: string;
  setShortcut: (value: string) => void;
  isShortcutOpen: boolean;
  setIsShortcutOpen: (value: boolean) => void;
  unreadMessageCount: number;
  setUnreadMessageCounts: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  customerLogos: Record<string, string>;
  setCustomerLogos: (value: Record<string, string>) => void;
  setIsExpanded: (value: boolean) => void;
}

const NavContext = createContext<NavState>({} as NavState);

export function useNavContext() {
  const context = useContext(NavContext);
  if (!context) {
    throw new Error('NavContext needs a NavProvider!');
  }
  return context;
}

export function NavProvider({
  children,
  globalAction,
  customerLogosFromLoader = {},
}: {
  children: ReactNode;
  globalAction?: ReactNode;
  activeNavItemId?: string;
  customerLogosFromLoader?: Record<string, string>;
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Checks for isLeftNavExpanded string in Local Storage if it exists
  useEffect(() => {
    const leftNavExpandedStateFromLocalStorage =
      localStorage.getItem('isLeftNavExpanded');

    if (leftNavExpandedStateFromLocalStorage) {
      setIsExpanded(leftNavExpandedStateFromLocalStorage === 'true');
    }
  }, []);

  // Saves last isLeftNavExpanded state in local storage
  useEffect(() => {
    const saveData = () => {
      localStorage.setItem('isLeftNavExpanded', `${isExpanded}`);
    };

    window.addEventListener('visibilitychange', saveData);
    return () => {
      window.removeEventListener('visibilitychange', saveData);
    };
  }, [isExpanded]);

  const toggle = useCallback(() => {
    setIsExpanded((state) => !state);
  }, []);

  const [shortcut, setShortcut] = useState('');
  const [isShortcutOpen, setIsShortcutOpen] = useState(false);
  const [customerLogos, setCustomerLogos] = useState(customerLogosFromLoader);

  // Unread message counts with workItemId used as key
  const [unreadMessageCounts, setUnreadMessageCounts] = useState<
    Record<string, number>
  >({});

  // Total unread message count
  const unreadMessageCount = useMemo(
    () =>
      Object.values(unreadMessageCounts).reduce(
        (total, count) => total + count,
        0,
      ),
    [unreadMessageCounts],
  );

  const context = useMemo(
    () => ({
      isExpanded,
      toggle,
      shortcut,
      setShortcut,
      isShortcutOpen,
      setIsShortcutOpen,
      unreadMessageCount,
      setUnreadMessageCounts,
      customerLogos,
      setCustomerLogos,
      setIsExpanded,
    }),

    [
      isExpanded,
      shortcut,
      isShortcutOpen,
      unreadMessageCount,
      globalAction,
      customerLogos,
      setCustomerLogos,
      setIsExpanded,
    ],
  );
  return <NavContext.Provider value={context}>{children}</NavContext.Provider>;
}
